import { useAuthStore } from '@/store/authStore'
import { useMemo, useCallback } from 'react'

/**
 * Hook per gestire i permessi utente in modo DINAMICO.
 * I permessi vengono caricati dal database tramite il ruolo dell'utente.
 * NON ci sono permessi hardcoded - tutto viene dal server.
 */
export function usePermissions() {
  const { user } = useAuthStore()

  // Array dei permessi dell'utente (dal server)
  const permissions = useMemo(() => {
    return user?.permissions || []
  }, [user?.permissions])

  /**
   * Verifica se l'utente ha un permesso specifico
   * @param permissionCode - Il codice del permesso da verificare
   */
  const hasPermission = useCallback(
    (permissionCode: string): boolean => {
      if (!user) return false
      // Super admin ha sempre tutti i permessi
      if (user.role === 'super_admin') return true
      return permissions.includes(permissionCode)
    },
    [user, permissions]
  )

  /**
   * Verifica se l'utente ha almeno uno dei permessi specificati
   * @param permissionCodes - Array di codici permesso
   */
  const hasAnyPermission = useCallback(
    (permissionCodes: string[]): boolean => {
      if (!user) return false
      if (user.role === 'super_admin') return true
      if (permissionCodes.length === 0) return true
      return permissionCodes.some((code) => permissions.includes(code))
    },
    [user, permissions]
  )

  /**
   * Verifica se l'utente ha tutti i permessi specificati
   * @param permissionCodes - Array di codici permesso
   */
  const hasAllPermissions = useCallback(
    (permissionCodes: string[]): boolean => {
      if (!user) return false
      if (user.role === 'super_admin') return true
      if (permissionCodes.length === 0) return true
      return permissionCodes.every((code) => permissions.includes(code))
    },
    [user, permissions]
  )

  /**
   * Verifica se l'utente ha almeno un permesso che inizia con un prefisso
   * @param prefix - Il prefisso da cercare (es: "admin." per tutti i permessi admin)
   */
  const hasPermissionStartingWith = useCallback(
    (prefix: string): boolean => {
      if (!user) return false
      if (user.role === 'super_admin') return true
      return permissions.some((p) => p.startsWith(prefix))
    },
    [user, permissions]
  )

  /**
   * Verifica se l'utente può accedere alle impostazioni di sistema
   * (solo super_admin - questo è l'unico controllo basato su ruolo)
   */
  const isSuperAdmin = useMemo(() => {
    return user?.role === 'super_admin'
  }, [user])

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasPermissionStartingWith,
    isSuperAdmin,
  }
}
